import { AxiosResponse } from 'axios';
import React, { useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import Button from '@/components/atoms/Button';
import useGetPlanInfo from '@/hooks/api/useGetPlanInfo';
import useGetUpgradeToken from '@/hooks/api/useGetUpgradeToken';
import useSetLoading from '@/hooks/useSetLoading';
import { UpgradeTokenInfoState, UpgradeTokenInfoStateType } from '@/states/UpgradeTokenInfoState';
import alertState from '@/states/alertState';
import { PlanInfoState, PlanInfoStateType } from '@/states/planInfoState';
import { userState } from '@/states/selectors/userState';
import { UpgradeTokenResponse } from '@/types/api/mypage/UpgradeTokenInfo';
/** 65262_マンション価格改定対応 2024/10/1～2025/7/31(9ヶ月目)以降削除可能です */
import { quitBeforePriceChangedState } from '@/states/selectors/quitBeforePriceChangedState';
/** 65262_マンション価格改定 [ここまで] */
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import { formatDate } from '@/utils/commonFunc';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';
import Router from 'next/router';
import ContractUpgrade from './ContractUpgradeInfo';
import jsxMovingBlockButton from './PlanInfoMoving';

import Heading from '../atoms/Heading';
import LinkButton from '../atoms/LinkButton';

type Props = {
  anchor: string;
};

const PlanInfo = ({ anchor }: Props) => {
  // ユーザー情報
  const user = useRecoilValue(userState);
  const planInfo = useRecoilValue<PlanInfoStateType>(PlanInfoState);
  const { execute: callAPIGetPlanInfo } = useGetPlanInfo();
  const { startLoading, finishLoading } = useSetLoading();
  const setAlert = useSetRecoilState(alertState);
  const { execute: getTokenInfo } = useGetUpgradeToken();
  const [upgradeTokenInfo, setUpgradeTokenInfo] =
    useRecoilState<UpgradeTokenInfoStateType>(UpgradeTokenInfoState);
  // マンション10Gアップグレード元の2G料金プランコード
  const isG10UpgMs2G = Constants.PLAN_CODE_FOR_2G_MANSION.includes(planInfo.planCode);
  // マンション10Gアップグレード先の10G料金プランコード
  const isG10UpgMs10G = Constants.PLAN_CODE_FOR_10G_MANSION.includes(planInfo.planCode);
  // 10Gアップグレードお申込みのお客様の表示
  const jsxContractUpgrade = ContractUpgrade();

  /** 65262_マンション価格改定対応 2024/10/1～2025/7/31(9ヶ月目)以降削除可能です */
  const isQuitBeforePriceChangedState = useRecoilValue(
    quitBeforePriceChangedState
  ).isQuitBeforePriceState;
  /** 65262_マンション価格改定 [ここまで] */

  useEffect((): void => {
    callAPIGetPlanInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 10G用マイページのみの表示
  // UGトークン情報取得APIの呼び出し
  useEffect(() => {
    const callAPIGetUpgradeToken = async () => {
      try {
        startLoading(ApiUrl.Read.UPGRADE_TOKEN);
        const { data }: AxiosResponse<UpgradeTokenResponse, any> = await getTokenInfo();
        // 取得した値をセット
        setUpgradeTokenInfo({
          ...upgradeTokenInfo,
          serviceid: data.service_id,
          ispcnno: data.isp_cn_no,
          linkstate: data.link_state,
          startdate: formatDate(data.start_date),
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          setUpgradeTokenInfo({
            ...upgradeTokenInfo,
            ispcnno: '-',
            startdate: '-',
          });
          return;
        }
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }
        setAlert({
          isOpen: true,
          apiType: Constants.ApiType.API_GET,
        });
      } finally {
        finishLoading(ApiUrl.Read.UPGRADE_TOKEN);
      }
    };
    // マンションの場合はプラン種別が分かれていない為、プラン種別と料金プランコードを条件とする
    if (planInfo.planType === '10Gs' || (planInfo.planType === 'mansion' && isG10UpgMs10G)) {
      callAPIGetUpgradeToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 回線申込キャンセル可能な契約ステータス一覧 */
  const canCancelStatus = ['S01', 'S02', 'S04'];

  /** 回線申込キャンセルボタン非活性判定 */
  const disableButton = () => {
    if (user.cnStatusCode === undefined) {
      return true;
    }
    return !canCancelStatus.includes(user.cnStatusCode);
  };

  /**
   * 「10Gアップグレード」の項目表示有無を取得します。
   * @returns 表示可能な場合は、trueを返却します。それ以外は、falseを返却します。
   */
  const isShow10gContractUpgrade = (): boolean => {
    // 「2g」の場合のみ表示する。
    // マンションの場合はプラン種別が分かれていない為、プラン種別と料金プランコードを条件とする
    if (planInfo.planType === '2G' || (planInfo.planType === 'mansion' && isG10UpgMs2G)) {
      return true;
    }
    return false;
  };

  const handleClickCancelButton = () => {
    Router.push('/contract/entry_cancel');
  };

  // 契約内容ブロック
  const jsxPlan = (): React.JSX.Element => {
    return (
      <div>
        <Heading as='h3'>ご契約の回線プラン</Heading>
        <div className='m-table'>
          <table>
            <tbody>
              <tr>
                <th className='m-table__th'>お申込プラン</th>
                <td className='m-table__td' data-testid='planName'>
                  {planInfo.planName}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>ご利用開始日</th>
                <td className='m-table__td' data-testid='serviceStartDate'>
                  {planInfo.serviceStartDate ?? '-'}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>ご利用期間</th>
                <td className='m-table__td' data-testid='contractTotalMonth'>
                  {planInfo.contractTotalMonth === 0 ? '-' : `${planInfo.contractTotalMonth}ヶ月`}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>申込日</th>
                <td className='m-table__td' data-testid='acceptDate'>
                  {planInfo.acceptDate}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>ご利用状況</th>
                <td className='m-table__td' data-testid='cnStatusName'>
                  {planInfo.cnStatusName}
                </td>
              </tr>
              {/** 次回更新月が「-」（契約期間なし）の場合は表示しない */}
              {/* {planInfo.contractUpdateMonth !== '-' && (
              <tr>
                <th className='m-table__th'>次回更新月</th>
                <td className='m-table__td' data-testid='contractUpdateMonth'>
                  {planInfo.contractUpdateMonth}
                </td>
              </tr>
            )} */}
              {(planInfo.planType === '10Gs' ||
                (planInfo.planType === 'mansion' && isG10UpgMs10G)) && (
                <tr>
                  <th className='m-table__th'>2Gプランお客様番号</th>
                  <td className='m-table__td' data-testid='ispcnno'>
                    {upgradeTokenInfo.ispcnno}
                  </td>
                </tr>
              )}
              {(planInfo.planType === '10Gs' ||
                (planInfo.planType === 'mansion' && isG10UpgMs10G)) && (
                <tr>
                  <th className='m-table__th'>2Gプラン利用開始日</th>
                  <td className='m-table__td' data-testid='startdate'>
                    {upgradeTokenInfo.startdate}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // 契約内容ブロックの下部ボタン
  const jsxPlanButton = (): React.JSX.Element => {
    const downloadPlanInfo = () => {
      (async () => {
        startLoading(ApiUrl.Read.DOWNLOAD_CONTRACT);

        const sigV4Headers = await getSigV4HeadersForGet(`${ApiUrl.Read.DOWNLOAD_CONTRACT}/full`);
        await api
          .get(`${ApiUrl.Read.DOWNLOAD_CONTRACT}/full`, {
            headers: sigV4Headers.headers,
          })
          .then(({ data }) => {
            const buffer = Buffer.from(data.data, 'base64');
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const downloadUrl = URL.createObjectURL(blob);
            const download = document.createElement('a');

            download.href = downloadUrl;
            download.download = data.file_name;
            download.click();

            (window.URL || window.webkitURL).revokeObjectURL(downloadUrl);
          })
          .catch((error: any) => {
            if (isExpiredToken(error)) {
              sessionStorage.clear();
              window.location.href = url('/login');
              return;
            }
            setAlert({
              isOpen: true,
              apiType: '',
            });
          })
          .finally(() => finishLoading(ApiUrl.Read.DOWNLOAD_CONTRACT));
      })();
    };
    return (
      <div className='m-button col2'>
        <Button type='button' variant='secondary' onClick={downloadPlanInfo}>
          ご契約内容の確認
        </Button>
        {/** 回線解約 */}
        {(planInfo.cnStatusCode === 'S05' || planInfo.cnStatusCode === 'S06') && (
          <LinkButton variant='secondary' href='/contract/quit_contract'>
            NURO 光 を解約する
          </LinkButton>
        )}
        {!user.isOpen && planInfo.canEntryCancel && (
          <Button variant='secondary' onClick={handleClickCancelButton} disabled={disableButton()}>
            回線申込キャンセル
          </Button>
        )}
        {planInfo.cnStatusCode === 'S07' && planInfo.canQuitCancel && (
          <LinkButton variant='secondary' href='/contract/quit_cancel'>
            NURO 光 解約内容の確認・解約を取消
          </LinkButton>
        )}
        {/** 65262_マンション価格改定対応 2024/10/1～2025/7/31(9ヶ月目)以降削除可能です */}
        {isQuitBeforePriceChangedState && (
          <ul className='m-list-notice'>
            <li className='m-list-notice__item infomation sub'>
              2024/10/1の料金改定に伴い、「ご契約内容の確認」内に記載の月額基本料金が
              改定後の金額となっている場合がございます。
              <br />
              改定前の料金が記載された契約書面をご希望のお客さまは、サポート窓口まで
              お問い合わせください。
            </li>
          </ul>
        )}
        {/** 65262_マンション価格改定 [ここまで] */}
      </div>
    );
  };

  return (
    <div className='m-box-02' id={anchor}>
      <div className='m-box-02__inner'>
        {/** ご契約内容 */}
        <Heading as='h2' icon='home'>
          ご契約内容
        </Heading>
        {/** 契約内容 */}
        {jsxPlan()}
        {jsxPlanButton()}
        {/** 引越し */}
        {!user.isMansion && jsxMovingBlockButton()}
        {/** 10Gアップグレード */}
        {isShow10gContractUpgrade() && jsxContractUpgrade}
      </div>
    </div>
  );
};

export default PlanInfo;
